// WHSResponsibilityStatement.tsx
// WHS Act 2011 (Qld) s.19 (PCBU duties) + s.28 (Worker duties)
// Priority 9 — WHS Module Revision v1.0 + v2.0
//
// Three tabs:
//   Template    — PCBU and worker obligation clauses, editable by WHSO (Tier C)
//   Issued      — All issued statements with sign-on status; mark as signed
//   Issue New   — Select workers and issue the statement; PDF generation
//
// Feeds: Inductions module (induction checklist includes RS sign-on)
// Tier: All users can view issued statements for their own records.
//       Tier C (WHSO) and above can create, edit, and sign.

import { useState, useMemo } from 'react'
import {
  FileText, Users, Send, CheckCircle, Clock, AlertTriangle,
  Plus, Download, Eye, X, Search, ChevronDown, ChevronUp,
  Shield, Pen, Trash2, RefreshCw, User,
} from 'lucide-react'
import { useAuthStore } from '../stores/auth.store'
import { buildDocumentHeader, buildDocumentFooter } from '../utils/documentBranding'

// ── Types ─────────────────────────────────────────────────────────

type RSStatus = 'ISSUED' | 'SIGNED' | 'SUPERSEDED'

interface IssuedStatement {
  id:           string
  version:      string
  workerName:   string
  workerId?:    string
  jobRef?:      string
  status:       RSStatus
  issuedBy:     string
  issuedAt:     string
  signedAt?:    string
  witnessName?: string
}

interface ObligationClause {
  id:       string
  text:     string
  ref?:     string   // legislative reference
}

// ── Standard obligation clauses (WHS Act 2011 Qld + WHS Regulation 2011) ─────

const DEFAULT_PCBU_OBLIGATIONS: ObligationClause[] = [
  { id: 'p1', ref: 's.19(1)', text: 'Provide and maintain a work environment without risks to health and safety, so far as is reasonably practicable.' },
  { id: 'p2', ref: 's.19(2)(a)', text: 'Provide and maintain safe plant and structures.' },
  { id: 'p3', ref: 's.19(2)(b)', text: 'Provide and maintain safe systems of work.' },
  { id: 'p4', ref: 's.19(2)(c)', text: 'Ensure the safe use, handling, and storage of plant, structures and substances.' },
  { id: 'p5', ref: 's.19(2)(d)', text: 'Provide adequate facilities for the welfare of workers at work, including ensuring access to those facilities.' },
  { id: 'p6', ref: 's.19(2)(e)', text: 'Provide any information, training, instruction or supervision that is necessary to protect all persons from risks to their health and safety arising from work carried out.' },
  { id: 'p7', ref: 's.19(2)(f)', text: 'Monitor the health of workers and the conditions at the workplace for the purpose of preventing illness or injury.' },
  { id: 'p8', ref: 's.47', text: 'Consult with workers so far as is reasonably practicable about matters relating to work health and safety that directly affect, or are likely to directly affect, them.' },
  { id: 'p9', ref: 'Reg. 39', text: 'Prepare, maintain, and implement emergency plans and ensure workers are trained in emergency procedures.' },
  { id: 'p10', ref: 'Reg. 291', text: 'Ensure a Safe Work Method Statement (SWMS) is prepared and implemented before high risk construction work commences, and that all workers understand and sign the SWMS.' },
]

const DEFAULT_WORKER_OBLIGATIONS: ObligationClause[] = [
  { id: 'w1', ref: 's.28(a)', text: 'Take reasonable care for my own health and safety while at work.' },
  { id: 'w2', ref: 's.28(b)', text: 'Take reasonable care that my acts or omissions do not adversely affect the health and safety of other persons.' },
  { id: 'w3', ref: 's.28(c)', text: 'Comply, so far as I am reasonably able, with any reasonable instruction given by the PCBU to allow the PCBU to comply with the WHS Act 2011 and WHS Regulation 2011.' },
  { id: 'w4', ref: 's.28(d)', text: 'Co-operate with any reasonable policy or procedure of the PCBU relating to health or safety at the workplace, if I have been notified of the policy or procedure.' },
  { id: 'w5', ref: 's.81', text: 'Report any hazard, near miss, injury, illness or dangerous incident to my supervisor or the Work Health and Safety Officer (WHSO) as soon as practicable.' },
  { id: 'w6', ref: 'Reg. 291', text: 'Read, understand, and comply with the Safe Work Method Statement (SWMS) relevant to my assigned tasks before commencing high risk construction work.' },
  { id: 'w7', ref: 's.84', text: 'Not misuse or interfere with anything provided at the workplace for the purposes of ensuring health, safety and welfare.' },
  { id: 'w8', ref: 'Reg. 39', text: 'Familiarise myself with emergency procedures, evacuation routes, and first aid arrangements for each workplace I attend.' },
]

// ── Mock seed data ────────────────────────────────────────────────

const NOW = new Date()
const dAgo = (d: number) => new Date(NOW.getTime() - d * 86400000).toISOString()

const MOCK_EMPLOYEES = [
  { id: 'E001', name: 'Dale Morrison',    role: 'Traffic Controller',        status: 'active' },
  { id: 'E002', name: 'Jayden Walsh',     role: 'Electrical Apprentice',     status: 'active' },
  { id: 'E003', name: 'Kayla Nguyen',     role: 'Site Supervisor',           status: 'active' },
  { id: 'E004', name: 'Ricky Thompson',   role: 'Plant Operator',            status: 'active' },
  { id: 'E005', name: 'Priya Sharma',     role: 'Electrician — Licensed',    status: 'active' },
  { id: 'E006', name: 'Tom Briscoe',      role: 'Labourer',                  status: 'active' },
  { id: 'E007', name: 'Amanda Osei',      role: 'Project Coordinator',       status: 'active' },
]

const INITIAL_ISSUED: IssuedStatement[] = [
  { id: 'RS-001', version: '1.0', workerName: 'Dale Morrison',  workerId: 'E001', jobRef: 'J-2026-003', status: 'SIGNED',  issuedBy: 'WHSO — Jane Smith',   issuedAt: dAgo(28), signedAt: dAgo(27), witnessName: 'Jane Smith' },
  { id: 'RS-002', version: '1.0', workerName: 'Jayden Walsh',   workerId: 'E002', jobRef: 'J-2026-003', status: 'SIGNED',  issuedBy: 'WHSO — Jane Smith',   issuedAt: dAgo(28), signedAt: dAgo(28), witnessName: 'Jane Smith' },
  { id: 'RS-003', version: '1.0', workerName: 'Ricky Thompson', workerId: 'E004', jobRef: 'J-2026-004', status: 'ISSUED',  issuedBy: 'WHSO — Jane Smith',   issuedAt: dAgo(5) },
  { id: 'RS-004', version: '1.0', workerName: 'Priya Sharma',   workerId: 'E005', jobRef: 'J-2026-004', status: 'ISSUED',  issuedBy: 'WHSO — Jane Smith',   issuedAt: dAgo(5) },
  { id: 'RS-005', version: '1.0', workerName: 'Tom Briscoe',    workerId: 'E006',                        status: 'SIGNED',  issuedBy: 'WHSO — Robert Chen',  issuedAt: dAgo(60), signedAt: dAgo(59), witnessName: 'Robert Chen' },
  { id: 'RS-006', version: '1.0', workerName: 'Kayla Nguyen',   workerId: 'E003', jobRef: 'J-2026-001', status: 'ISSUED',  issuedBy: 'WHSO — Jane Smith',   issuedAt: dAgo(2) },
]

// ── Helpers ────────────────────────────────────────────────────────

const fmt = (iso?: string) => iso ? new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
const fmtTime = (iso?: string) => iso ? new Date(iso).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'

const statusBadge = (s: RSStatus) => {
  const map: Record<RSStatus, { label: string; cls: string }> = {
    ISSUED:     { label: 'Issued — awaiting signature', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
    SIGNED:     { label: 'Signed',  cls: 'bg-green-100 text-green-800 border-green-200' },
    SUPERSEDED: { label: 'Superseded', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
  }
  const m = map[s]
  return <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-0.5 rounded-full border ${m.cls}`}>{m.label}</span>
}

// ── PDF generation ────────────────────────────────────────────────

function generateRSPDF(
  stmt: IssuedStatement,
  pcbu: ObligationClause[],
  worker: ObligationClause[],
  version: string
) {
  const now = new Date().toLocaleDateString('en-AU', { day: '2-digit', month: 'long', year: 'numeric' })
  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>WHS Responsibility Statement — ${stmt.workerName}</title>
<style>
*{box-sizing:border-box}
body{font-family:Arial,sans-serif;font-size:10.5pt;color:#111;margin:20mm 22mm}
h2{font-size:15pt;font-weight:900;color:#1F3864;margin:0 0 4px;text-transform:uppercase;letter-spacing:.5px}
.doc-title{border-bottom:3px solid #2E75B6;padding-bottom:8px;margin-bottom:14px}
.subtitle{font-size:9.5pt;color:#555;margin-bottom:0}
.section{margin-bottom:18px}
.section-hdr{font-size:11pt;font-weight:bold;color:#1F3864;border-left:4px solid #2E75B6;padding-left:8px;margin-bottom:8px}
.leg-ref{font-size:8pt;color:#888;font-style:italic;margin-right:6px;white-space:nowrap}
ol{margin:0;padding-left:20px}
li{margin-bottom:5px;line-height:1.5}
.detail-grid{display:grid;grid-template-columns:1fr 1fr;gap:0;border:1px solid #ccc;margin-bottom:18px}
.detail-cell{padding:6px 10px;border-bottom:1px solid #eee}
.detail-cell:nth-child(odd){background:#f7f8fa;font-weight:bold;color:#555;font-size:9pt}
.detail-cell:nth-child(even){font-size:9.5pt}
.sig-section{margin-top:24px;display:grid;grid-template-columns:1fr 1fr;gap:30px}
.sig-box{border:1.5px solid #999;border-radius:4px;padding:12px 14px;min-height:80px}
.sig-label{font-size:8.5pt;font-weight:bold;color:#555;text-transform:uppercase;margin-bottom:4px}
.sig-line{border-bottom:1px solid #555;height:28px;margin:8px 0 4px}
.sig-name{font-size:8pt;color:#777}
.statement-box{background:#FFF8E1;border:1.5px solid #F9A825;border-radius:4px;padding:12px 16px;margin-top:18px;font-size:10pt;font-style:italic;color:#444}
.version-tag{font-size:8pt;color:#aaa;text-align:right;margin-top:14px}
</style></head><body>
${buildDocumentHeader('INVOICE')}
<div class="doc-title">
  <h2>WHS Responsibility Statement</h2>
  <div class="subtitle">Work Health and Safety Act 2011 (Qld) &mdash; Sections 19 and 28 &nbsp;&bull;&nbsp; Version ${version}</div>
</div>

<div class="section">
<div class="detail-grid">
  <div class="detail-cell">Worker name</div><div class="detail-cell">${stmt.workerName}</div>
  <div class="detail-cell">Issued by</div><div class="detail-cell">${stmt.issuedBy}</div>
  <div class="detail-cell">Date issued</div><div class="detail-cell">${fmt(stmt.issuedAt)}</div>
  <div class="detail-cell">Job reference</div><div class="detail-cell">${stmt.jobRef ?? 'Site-wide — all active jobs'}</div>
  <div class="detail-cell">Document reference</div><div class="detail-cell">${stmt.id}</div>
  <div class="detail-cell">Status</div><div class="detail-cell">${stmt.status}${stmt.signedAt ? ' — Signed ' + fmt(stmt.signedAt) : ' — Awaiting signature'}</div>
</div>
</div>

<div class="section">
<div class="section-hdr">Part A &mdash; Obligations of the PCBU (MCB Civil Electrical Pty Ltd)</div>
<p style="font-size:9.5pt;color:#555;margin-bottom:8px">As a Person Conducting a Business or Undertaking (PCBU), MCB Civil Electrical Pty Ltd acknowledges and undertakes to fulfil the following duties:</p>
<ol>
${pcbu.map(c => `<li><span class="leg-ref">${c.ref || ''}</span>${c.text}</li>`).join('\n')}
</ol>
</div>

<div class="section">
<div class="section-hdr">Part B &mdash; Obligations of the Worker</div>
<p style="font-size:9.5pt;color:#555;margin-bottom:8px">As a worker engaged by MCB Civil Electrical Pty Ltd, I acknowledge and undertake to fulfil the following duties:</p>
<ol>
${worker.map(c => `<li><span class="leg-ref">${c.ref || ''}</span>${c.text}</li>`).join('\n')}
</ol>
</div>

<div class="statement-box">
By signing below, I, <strong>${stmt.workerName}</strong>, confirm that I have read and understood this WHS Responsibility Statement in full. I understand my obligations under the Work Health and Safety Act 2011 (Qld) and the Work Health and Safety Regulation 2011 (Qld). I have had the opportunity to ask questions and any questions have been answered to my satisfaction.
</div>

<div class="sig-section">
  <div class="sig-box">
    <div class="sig-label">Worker signature</div>
    <div class="sig-line">${stmt.signedAt ? '✓ Signed electronically' : ''}</div>
    <div class="sig-name">${stmt.workerName}</div>
    <div class="sig-name" style="margin-top:4px">Date: ${fmt(stmt.signedAt) ?? '______ / ______ / ______'}</div>
  </div>
  <div class="sig-box">
    <div class="sig-label">PCBU representative (WHSO / PM)</div>
    <div class="sig-line">${stmt.witnessName ? '✓ Signed electronically' : ''}</div>
    <div class="sig-name">${stmt.witnessName ?? stmt.issuedBy}</div>
    <div class="sig-name" style="margin-top:4px">Date: ${fmt(stmt.signedAt) ?? '______ / ______ / ______'}</div>
  </div>
</div>

<div class="version-tag">MCBCE-K &bull; WHS Responsibility Statement &bull; ${stmt.id} &bull; Version ${version} &bull; Generated ${now}</div>
${buildDocumentFooter('INVOICE', stmt.id)}
</body></html>`

  const w = window.open('', '_blank', 'width=1000,height=820')
  if (!w) { alert('Pop-up blocked — allow pop-ups to generate PDFs'); return }
  w.document.write(html)
  w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}

// ── Component ──────────────────────────────────────────────────────

export function WHSResponsibilityStatement() {
  const { isTier } = useAuthStore()
  const canEdit = isTier('C_WHSO') || isTier('B_PM') || isTier('B_PC') || isTier('A_DEVELOPER')

  // Template state
  const [pcbuClauses, setPcbuClauses] = useState<ObligationClause[]>(DEFAULT_PCBU_OBLIGATIONS)
  const [workerClauses, setWorkerClauses] = useState<ObligationClause[]>(DEFAULT_WORKER_OBLIGATIONS)
  const [version, setVersion] = useState('1.0')
  const [editingClause, setEditingClause] = useState<{ type: 'pcbu'|'worker'; id: string } | null>(null)
  const [editText, setEditText] = useState('')

  // Issued statements
  const [issued, setIssued] = useState<IssuedStatement[]>(INITIAL_ISSUED)
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState<RSStatus|'ALL'>('ALL')
  const [selectedStmt, setSelectedStmt] = useState<IssuedStatement | null>(null)
  const [signConfirm, setSignConfirm] = useState<IssuedStatement | null>(null)
  const [witnessName, setWitnessName] = useState('')

  // Issue new
  const [selectedWorkers, setSelectedWorkers] = useState<string[]>([])
  const [issueJobRef, setIssueJobRef] = useState('')
  const [issuing, setIssuing] = useState(false)
  const [issueSuccess, setIssueSuccess] = useState<string[]>([])

  // Tab
  const [tab, setTab] = useState<'template'|'issued'|'issue'>('issued')

  // ── Derived ──────────────────────────────────────────────────────
  const filtered = useMemo(() => issued.filter(s => {
    const matchSearch = !search || s.workerName.toLowerCase().includes(search.toLowerCase()) || (s.jobRef ?? '').toLowerCase().includes(search.toLowerCase())
    const matchStatus = filterStatus === 'ALL' || s.status === filterStatus
    return matchSearch && matchStatus
  }), [issued, search, filterStatus])

  const pendingCount = issued.filter(s => s.status === 'ISSUED').length
  const signedCount  = issued.filter(s => s.status === 'SIGNED').length

  // ── Handlers ────────────────────────────────────────────────────
  function startEdit(type: 'pcbu'|'worker', clause: ObligationClause) {
    setEditingClause({ type, id: clause.id })
    setEditText(clause.text)
  }

  function saveEdit() {
    if (!editingClause) return
    if (editingClause.type === 'pcbu') {
      setPcbuClauses(cs => cs.map(c => c.id === editingClause.id ? { ...c, text: editText } : c))
    } else {
      setWorkerClauses(cs => cs.map(c => c.id === editingClause.id ? { ...c, text: editText } : c))
    }
    setEditingClause(null)
  }

  function deleteClause(type: 'pcbu'|'worker', id: string) {
    if (type === 'pcbu') setPcbuClauses(cs => cs.filter(c => c.id !== id))
    else setWorkerClauses(cs => cs.filter(c => c.id !== id))
  }

  function addClause(type: 'pcbu'|'worker') {
    const newId = `${type === 'pcbu' ? 'p' : 'w'}${Date.now()}`
    const newClause = { id: newId, text: 'New obligation — click to edit', ref: '' }
    if (type === 'pcbu') setPcbuClauses(cs => [...cs, newClause])
    else setWorkerClauses(cs => [...cs, newClause])
    startEdit(type, newClause)
  }

  function markSigned(stmt: IssuedStatement) {
    if (!witnessName.trim()) return
    setIssued(prev => prev.map(s => s.id === stmt.id
      ? { ...s, status: 'SIGNED', signedAt: new Date().toISOString(), witnessName: witnessName.trim() }
      : s
    ))
    setSignConfirm(null)
    setWitnessName('')
  }

  function issueToWorkers() {
    if (!selectedWorkers.length) return
    setIssuing(true)
    const newStmts: IssuedStatement[] = selectedWorkers.map(wid => {
      const emp = MOCK_EMPLOYEES.find(e => e.id === wid)!
      const id  = `RS-${String(issued.length + newStmts?.length + 1 || issued.length + 1).padStart(3, '0')}`
      return {
        id: `RS-${Date.now()}-${wid}`,
        version,
        workerName: emp.name,
        workerId:   wid,
        jobRef:     issueJobRef || undefined,
        status:     'ISSUED' as RSStatus,
        issuedBy:   'WHSO — Jane Smith',
        issuedAt:   new Date().toISOString(),
      }
    })
    setIssued(prev => [...prev, ...newStmts])
    setIssueSuccess(selectedWorkers.map(wid => MOCK_EMPLOYEES.find(e => e.id === wid)!.name))
    setSelectedWorkers([])
    setIssueJobRef('')
    setIssuing(false)
    setTimeout(() => setIssueSuccess([]), 4000)
  }

  const alreadyIssued = (wid: string) => issued.some(s => s.workerId === wid && s.status !== 'SUPERSEDED')

  // ── Render ──────────────────────────────────────────────────────
  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5">

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#2E75B6]" />
            WHS Responsibility Statements
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Work Health and Safety Act 2011 (Qld) — ss.19 &amp; 28 &nbsp;·&nbsp; Issued at induction, signed before field work commences</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 text-xs text-amber-800 font-medium flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" />{pendingCount} awaiting signature
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-1.5 text-xs text-green-800 font-medium flex items-center gap-1.5">
            <CheckCircle className="w-3.5 h-3.5" />{signedCount} signed
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {([
          { key: 'issued',   label: 'Issued Statements', icon: FileText, badge: pendingCount || undefined },
          { key: 'issue',    label: 'Issue to Workers',  icon: Send,     badge: undefined as number | undefined },
          { key: 'template', label: 'Statement Template', icon: Pen,     badge: undefined as number | undefined },
        ] as const).map(({ key, label, icon: Icon, badge }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors relative -mb-px ${
              tab === key ? 'border-[#2E75B6] text-[#2E75B6]' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            <Icon className="w-4 h-4" />{label}
            {badge ? <span className="ml-1 bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{badge}</span> : null}
          </button>
        ))}
      </div>

      {/* ── TAB: Issued Statements ── */}
      {tab === 'issued' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search worker or job…" className="input pl-9 text-sm w-full" />
            </div>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value as any)} className="input text-sm w-40">
              <option value="ALL">All statuses</option>
              <option value="ISSUED">Awaiting signature</option>
              <option value="SIGNED">Signed</option>
              <option value="SUPERSEDED">Superseded</option>
            </select>
          </div>

          {/* Table */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Ref', 'Worker', 'Job', 'Issued', 'Status', 'Signed', ''].map(h => (
                    <th key={h} className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-10 text-gray-400 text-sm">No statements match the current filter.</td></tr>
                )}
                {filtered.map(stmt => (
                  <tr key={stmt.id} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">{stmt.id}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{stmt.workerName}</div>
                      <div className="text-xs text-gray-400">v{stmt.version}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{stmt.jobRef ?? <span className="italic text-gray-400">Site-wide</span>}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{fmt(stmt.issuedAt)}</td>
                    <td className="px-4 py-3">{statusBadge(stmt.status)}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {stmt.signedAt ? <>{fmt(stmt.signedAt)}<div className="text-gray-400">wit. {stmt.witnessName}</div></> : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => generateRSPDF(stmt, pcbuClauses, workerClauses, version)}
                          className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 transition-colors"
                          title="Print / PDF">
                          <Download className="w-3.5 h-3.5" />PDF
                        </button>
                        {canEdit && stmt.status === 'ISSUED' && (
                          <button onClick={() => { setSignConfirm(stmt); setWitnessName('') }}
                            className="text-xs bg-green-600 hover:bg-green-700 text-white px-2.5 py-1 rounded font-medium flex items-center gap-1 transition-colors">
                            <Pen className="w-3 h-3" />Sign
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Compliance notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-800 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <span>All workers must receive and sign a WHS Responsibility Statement before commencing field work. Unsigned statements represent a compliance gap. Workers with <strong>ISSUED</strong> status must sign before site access is permitted.</span>
          </div>
        </div>
      )}

      {/* ── TAB: Issue to Workers ── */}
      {tab === 'issue' && (
        <div className="space-y-5">
          {issueSuccess.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-green-800">
                <strong>Issued successfully.</strong> Statement issued to: {issueSuccess.join(', ')}. Workers will be prompted to sign at their next pre-start.
              </div>
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
            <h3 className="font-medium text-gray-900">Select workers to issue</h3>
            <p className="text-sm text-gray-500">Workers shown in amber already have an active statement. You can re-issue to supersede a previous version.</p>

            <div className="grid grid-cols-1 gap-2">
              {MOCK_EMPLOYEES.map(emp => {
                const hasActive = alreadyIssued(emp.id)
                const checked   = selectedWorkers.includes(emp.id)
                return (
                  <label key={emp.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      checked ? 'border-[#2E75B6] bg-blue-50/40' : hasActive ? 'border-amber-200 bg-amber-50/30' : 'border-gray-200 hover:bg-gray-50'
                    }`}>
                    <input type="checkbox" checked={checked}
                      onChange={e => setSelectedWorkers(prev => e.target.checked ? [...prev, emp.id] : prev.filter(id => id !== emp.id))}
                      className="rounded border-gray-300 text-[#2E75B6] focus:ring-[#2E75B6]" />
                    <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900">{emp.name}</div>
                      <div className="text-xs text-gray-500">{emp.role}</div>
                    </div>
                    {hasActive && (
                      <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium border border-amber-200">Has active statement</span>
                    )}
                  </label>
                )
              })}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Job reference <span className="text-gray-400 font-normal">(optional — leave blank for site-wide)</span></label>
              <input value={issueJobRef} onChange={e => setIssueJobRef(e.target.value)} placeholder="e.g. J-2026-005" className="input text-sm w-64" />
            </div>

            <div className="pt-2 border-t border-gray-100 flex items-center justify-between gap-4">
              <span className="text-sm text-gray-500">{selectedWorkers.length} worker{selectedWorkers.length !== 1 ? 's' : ''} selected</span>
              <button onClick={issueToWorkers} disabled={!selectedWorkers.length || !canEdit}
                className="btn-primary flex items-center gap-2 disabled:opacity-40">
                <Send className="w-4 h-4" />Issue statement{selectedWorkers.length > 1 ? 's' : ''}
              </button>
            </div>
          </div>

          {!canEdit && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800 flex items-center gap-2">
              <Shield className="w-4 h-4 flex-shrink-0" />
              Issuing responsibility statements requires WHSO (Tier C) access or above.
            </div>
          )}
        </div>
      )}

      {/* ── TAB: Template ── */}
      {tab === 'template' && (
        <div className="space-y-5">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-medium text-gray-900">Template version</h3>
                <p className="text-xs text-gray-400 mt-0.5">Changing the version number marks all existing issued statements for re-issue when you next generate PDFs.</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Version</span>
                <input value={version} onChange={e => setVersion(e.target.value)} disabled={!canEdit}
                  className="input text-sm w-20 text-center font-mono" />
              </div>
            </div>
          </div>

          {/* PCBU clauses */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="bg-[#1F3864] text-white px-5 py-3 flex items-center justify-between">
              <div>
                <div className="font-semibold text-sm">Part A — PCBU Obligations</div>
                <div className="text-xs text-blue-200 mt-0.5">WHS Act 2011 (Qld) s.19 — duties of a person conducting a business or undertaking</div>
              </div>
              {canEdit && (
                <button onClick={() => addClause('pcbu')}
                  className="text-xs bg-white/20 hover:bg-white/30 text-white px-2.5 py-1 rounded flex items-center gap-1 transition-colors">
                  <Plus className="w-3.5 h-3.5" />Add clause
                </button>
              )}
            </div>
            <div className="divide-y divide-gray-100">
              {pcbuClauses.map((clause, i) => (
                <div key={clause.id} className="px-5 py-3 flex items-start gap-3 hover:bg-gray-50/60">
                  <span className="text-sm font-bold text-[#2E75B6] w-6 flex-shrink-0 mt-0.5">{i + 1}.</span>
                  <div className="flex-1 min-w-0">
                    {editingClause?.id === clause.id ? (
                      <div className="space-y-2">
                        <textarea value={editText} onChange={e => setEditText(e.target.value)}
                          rows={3} className="input text-sm w-full resize-none" />
                        <div className="flex gap-2">
                          <button onClick={saveEdit} className="text-xs bg-[#2E75B6] text-white px-3 py-1 rounded font-medium hover:bg-[#1F3864]">Save</button>
                          <button onClick={() => setEditingClause(null)} className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-800 leading-relaxed">
                        {clause.ref && <span className="text-xs text-gray-400 italic mr-1.5">{clause.ref}</span>}
                        {clause.text}
                      </p>
                    )}
                  </div>
                  {canEdit && editingClause?.id !== clause.id && (
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => startEdit('pcbu', clause)} className="p-1 text-gray-400 hover:text-[#2E75B6] rounded transition-colors"><Pen className="w-3.5 h-3.5" /></button>
                      <button onClick={() => deleteClause('pcbu', clause.id)} className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Worker clauses */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="bg-[#2E75B6] text-white px-5 py-3 flex items-center justify-between">
              <div>
                <div className="font-semibold text-sm">Part B — Worker Obligations</div>
                <div className="text-xs text-blue-100 mt-0.5">WHS Act 2011 (Qld) s.28 — duties of workers</div>
              </div>
              {canEdit && (
                <button onClick={() => addClause('worker')}
                  className="text-xs bg-white/20 hover:bg-white/30 text-white px-2.5 py-1 rounded flex items-center gap-1 transition-colors">
                  <Plus className="w-3.5 h-3.5" />Add clause
                </button>
              )}
            </div>
            <div className="divide-y divide-gray-100">
              {workerClauses.map((clause, i) => (
                <div key={clause.id} className="px-5 py-3 flex items-start gap-3 hover:bg-gray-50/60">
                  <span className="text-sm font-bold text-[#2E75B6] w-6 flex-shrink-0 mt-0.5">{i + 1}.</span>
                  <div className="flex-1 min-w-0">
                    {editingClause?.id === clause.id ? (
                      <div className="space-y-2">
                        <textarea value={editText} onChange={e => setEditText(e.target.value)}
                          rows={3} className="input text-sm w-full resize-none" />
                        <div className="flex gap-2">
                          <button onClick={saveEdit} className="text-xs bg-[#2E75B6] text-white px-3 py-1 rounded font-medium hover:bg-[#1F3864]">Save</button>
                          <button onClick={() => setEditingClause(null)} className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-800 leading-relaxed">
                        {clause.ref && <span className="text-xs text-gray-400 italic mr-1.5">{clause.ref}</span>}
                        {clause.text}
                      </p>
                    )}
                  </div>
                  {canEdit && editingClause?.id !== clause.id && (
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => startEdit('worker', clause)} className="p-1 text-gray-400 hover:text-[#2E75B6] rounded transition-colors"><Pen className="w-3.5 h-3.5" /></button>
                      <button onClick={() => deleteClause('worker', clause.id)} className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {!canEdit && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800 flex items-center gap-2">
              <Shield className="w-4 h-4 flex-shrink-0" />
              Editing the responsibility statement template requires WHSO (Tier C) access or above.
            </div>
          )}
        </div>
      )}

      {/* ── Sign confirmation modal ── */}
      {signConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Record signature</h2>
                <p className="text-sm text-gray-500 mt-0.5">Confirm that <strong>{signConfirm.workerName}</strong> has read and signed the WHS Responsibility Statement in person.</p>
              </div>
              <button onClick={() => setSignConfirm(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-xs text-blue-800 space-y-1">
              <div><strong>Document:</strong> {signConfirm.id} · Version {signConfirm.version}</div>
              <div><strong>Issued:</strong> {fmtTime(signConfirm.issuedAt)}</div>
              <div><strong>Job:</strong> {signConfirm.jobRef ?? 'Site-wide'}</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Witness name (WHSO / PM) <span className="text-red-500">*</span></label>
              <input value={witnessName} onChange={e => setWitnessName(e.target.value)}
                placeholder="Enter your name as PCBU representative"
                className="input text-sm w-full" />
            </div>

            <div className="flex gap-3 pt-2">
              <button onClick={() => markSigned(signConfirm)} disabled={!witnessName.trim()}
                className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-40 py-2.5">
                <CheckCircle className="w-4 h-4" />Confirm signature recorded
              </button>
              <button onClick={() => setSignConfirm(null)} className="btn-secondary flex-1 py-2.5">Cancel</button>
            </div>

            <p className="text-xs text-gray-400 text-center">By confirming, you certify that the worker signed this document in your presence. This action is logged with a timestamp.</p>
          </div>
        </div>
      )}
    </div>
  )
}
